'use client';

import { useCallback, useRef, useState } from 'react';

import Close from '@icons/Close.svg';

import { Label } from '@components/Common';

import { InputProps } from './interfaces';

import {
  ErrorLabel,
  IconWrapper,
  InputContainer,
  InputField,
  InputWrapper,
} from './styles';

/**
 * Input component renders a styled input field with different variants.
 */
export const Input = ({
  label,
  placeholder,
  hasError,
  id,
  errorLabel,
  type,
  name,
  required,
  disabled,
  autoFocus,
  icon,
  hasIcon,
  value,
  onChange,
  onBlur,
  onSearch,
  onClear,
  variant,
  isModalOpen,
  color,
  backgroundColor,
}: InputProps) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const inputRefCallback = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      // Focus when the element is mounted and autoFocus is enabled
      if (node && autoFocus) {
        // Use requestAnimationFrame for better timing than setTimeout
        requestAnimationFrame(() => {
          node.focus();
        });
      }
    },
    [autoFocus],
  );

  const handleFocus = () => setIsFocused(true);
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (onChange) onChange(e);
  };

  const handleClear = () => {
    setInputValue('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
    if (onClear) onClear();
    if (onSearch) onSearch('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch(inputValue);
    }
  };

  const isSearch = variant === 'search';

  return (
    <InputContainer>
      {!isSearch && <Label htmlFor={id}>{label}</Label>}
      {hasError && <ErrorLabel id={`${id}-error`}>{errorLabel}</ErrorLabel>}
      <InputWrapper>
        {icon && (
          <IconWrapper position="left" $isFocused={isFocused}>
            {icon}
          </IconWrapper>
        )}
        <InputField
          ref={inputRefCallback}
          id={id}
          aria-describedby={hasError ? `${id}-error` : undefined}
          type={type}
          placeholder={placeholder}
          $hasError={hasError}
          $isSearch={isSearch}
          $variant={variant}
          $color={color}
          $backgroundColor={backgroundColor}
          name={name}
          required={required}
          $hasIcon={hasIcon}
          value={value !== undefined ? value : inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          autoFocus={autoFocus && !disabled}
        />
        {((isSearch && inputValue) || isModalOpen) && (
          <IconWrapper
            position="right"
            onClick={isModalOpen ? onClear : handleClear}
            $isFocused={isFocused}>
            <Close />
          </IconWrapper>
        )}
      </InputWrapper>
    </InputContainer>
  );
};
